import React, { useEffect, useState } from 'react';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Box,
    Spinner,
    Button,
    Input,
    Select,
    VStack,
    FormControl,
    FormLabel,
    useToast,
    FormErrorMessage,
} from '@chakra-ui/react';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../lib/profileContext';
import fetchWrapper from '../lib/fetch-wrapper';
import { url } from '../lib/constants';

const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const UserRolesManager = () => {
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('admin');
    const [editingUser, setEditingUser] = useState<string | null>(null);
    const [newRole, setNewRole] = useState('admin');
    const toast = useToast();

    const { instance, accounts }: { instance: IPublicClientApplication, accounts: AccountInfo[] } = useMsal();
    const { profile } = useProfile();

    useEffect(() => {
        void fetchUserRoles();
    }, []);

    const fetchUserRoles = async () => {
        setLoading(true);
        try {
            const fw = new fetchWrapper();
            const res = await fw.getUserRoles(`${url}/api/user-roles`);
            setUserRoles(res.data);
        } catch (error: any) {
            console.error("Error fetching user roles:", error);
            toast({
                title: "Error fetching user roles",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async () => {
        if (!email.trim()) {
            toast({
                title: "Email is required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (!isValidEmail(email.trim())) {
            toast({
                title: "Invalid Email",
                description: "Please enter a valid email address.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            const fw = new fetchWrapper();
            await fw.createUserRole(`${url}/api/user-roles`, email.trim(), role);
            toast({
                title: "User added successfully",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            setEmail('');
            setRole('admin');
            fetchUserRoles();
        } catch (error: any) {
            console.error("Error adding user:", error);
            toast({
                title: "Error adding user",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleDeleteUser = async (userEmail: string) => {
        // Add a native confirmation modal before proceeding
        const confirmDelete = window.confirm(`Are you sure you want to delete the user with email: ${userEmail}?`);
        if (!confirmDelete) return;

        try {
            const fw = new fetchWrapper();
            await fw.deleteUserRole(`${url}/api/user-roles`, userEmail);
            toast({
                title: "User deleted successfully",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            fetchUserRoles();
        } catch (error: any) {
            console.error("Error deleting user:", error);
            toast({
                title: "Error deleting user",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleEditUser = async () => {
        if (!editingUser) return;
        try {
            const fw = new fetchWrapper();
            await fw.editUserRole(`${url}/api/user-roles`, editingUser, newRole);
            toast({
                title: "User role updated successfully",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            setEditingUser(null);
            setNewRole('admin');
            void fetchUserRoles();
        } catch (error: any) {
            console.error("Error updating user role:", error);
            toast({
                title: "Error updating user role",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={4}>
                <Spinner size="xl" />
            </Box>
        );
    }

    return (
        <Box mt={4} height="100%" overflowX="auto">
            <VStack spacing={4} align="start" mb={8}>
                <FormControl isRequired isInvalid={email !== '' && !isValidEmail(email)}>
                    <FormLabel>Email</FormLabel>
                    <Input
                        placeholder="Enter user email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <FormErrorMessage>Invalid email format</FormErrorMessage>
                </FormControl>
                <FormControl>
                    <FormLabel>Role</FormLabel>
                    <Select value={role} onChange={(e) => setRole(e.target.value)}>
                        <option value="admin">Admin</option>
                        <option value="powerbi-viewer">PowerBI Viewer</option>
                    </Select>
                </FormControl>
                <Button variant="brand" onClick={handleAddUser}>
                    Add User
                </Button>
            </VStack>

            <Table variant="striped" colorScheme="gray">
                <Thead>
                    <Tr>
                        <Th>Email</Th>
                        <Th>Role</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {userRoles.map((user: any) => (
                        <Tr key={user.email}>
                            <Td>{user.email}</Td>
                            <Td>
                                {editingUser === user.email ? (
                                    <Select
                                        value={newRole}
                                        onChange={(e) => setNewRole(e.target.value)}
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="powerbi-viewer">PowerBI Viewer</option>
                                    </Select>
                                ) : (
                                    user.role
                                )}
                            </Td>
                            <Td>
                                {editingUser === user.email ? (
                                    <Button variant="brand" onClick={handleEditUser}>
                                        Save
                                    </Button>
                                ) : (
                                    <Button
                                        variant="brand"
                                        onClick={() => {
                                            setEditingUser(user.email);
                                            setNewRole(user.role);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                )}
                                <Button
                                    ml={2}
                                    variant="cancel"
                                    onClick={() => handleDeleteUser(user.email)}
                                >
                                    Delete
                                </Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </Box>
    );
};

export default UserRolesManager;
